import * as React from 'react';

/** A catalog occupation the parent makes available to pick from. */
export type AvailableOccupation = { id: string; name: string };

/** The learner's current-role value: a catalog occupation, free text, or unset. */
export type CurrentRoleValue = { id: string; name: string } | { name: string } | null;

/**
 * Sentinel key for the free-text (user-generated) option. It covers both the
 * synthetic "Add '<input>'" option and, once that value is saved, the option
 * representing it -- deliberately the SAME key. CDS Autocomplete keeps the
 * key the learner picked as its internal `selectedKey`; if that key ever
 * disappears from the option list, react-stately resolves it to `''`, wipes
 * the input, and desyncs its `lastValue` -- which leaves the popover
 * reopening itself on every subsequent render (LO-5081).
 */
export const CREATE_USER_GENERATED_KEY = '__personalization_current_role_create_user_generated__';

export type CurrentRoleOption = {
  key: string;
  label: string;
  value: CurrentRoleValue;
  /** True only for free text the learner has not applied yet -- renders as `Add "…"`. */
  isCreate: boolean;
};

type UseCurrentRoleEditorArgs = {
  /** Currently selected occupation (controlled). */
  value: CurrentRoleValue;
  /** Called with the next value whenever a selection replaces the current one. */
  onChange: (value: CurrentRoleValue) => void;
  /** Catalog occupations the learner can choose from, supplied by the parent. */
  availableOccupations: readonly AvailableOccupation[];
  /** The current typeahead input. */
  input: string;
};

const normalize = (text: string) => text.trim().toLowerCase();

const isUserGenerated = (value: CurrentRoleValue): value is { name: string } => !!value && !('id' in value);

const isSameValue = (a: CurrentRoleValue, b: CurrentRoleValue) => {
  if (!a || !b) return a === b;
  if ('id' in a || 'id' in b) return 'id' in a && 'id' in b && a.id === b.id;
  return a.name === b.name;
};

/**
 * Builds the options for the "Current role" typeahead and resolves selections.
 * Single-select — every selection or applied free text REPLACES `value`,
 * unlike the multi-select "Desired skills" typeahead this mirrors visually.
 */
export const useCurrentRoleEditor = ({ value, onChange, availableOccupations, input }: UseCurrentRoleEditorArgs) => {
  const trimmedInput = input.trim();
  const currentValueName = value?.name ?? '';
  const userGeneratedValueName = isUserGenerated(value) ? value.name : '';

  const options = React.useMemo<CurrentRoleOption[]>(() => {
    // Ignore the input as a filter until the learner actually types something
    // different from the current value -- otherwise merely opening the
    // dropdown on an already-filled field (input pre-seeded with the current
    // value's name) would narrow the list down to just that one match.
    const hasTypedNewQuery = normalize(input) !== normalize(currentValueName);
    const normalizedInput = hasTypedNewQuery ? normalize(input) : '';

    const catalogOptions: CurrentRoleOption[] = availableOccupations
      .filter((occupation) => !normalizedInput || occupation.name.toLowerCase().includes(normalizedInput))
      .map((occupation) => ({
        key: occupation.id,
        label: occupation.name,
        value: { id: occupation.id, name: occupation.name },
        isCreate: false,
      }));

    const matchesExistingLabel = availableOccupations.some(
      (occupation) => normalize(occupation.name) === normalizedInput
    );

    // Free text the learner is about to add, or -- once applied -- the saved
    // free-text value itself. Always under CREATE_USER_GENERATED_KEY so the
    // key the learner picked never leaves the list. Catalog keys need no
    // equivalent: once applied, `hasTypedNewQuery` is false and the whole
    // catalog is offered again, so a catalog key is always present.
    const userGeneratedOption: CurrentRoleOption[] = (() => {
      if (hasTypedNewQuery) {
        return trimmedInput && !matchesExistingLabel
          ? [{ key: CREATE_USER_GENERATED_KEY, label: trimmedInput, value: { name: trimmedInput }, isCreate: true }]
          : [];
      }
      return userGeneratedValueName
        ? [
            {
              key: CREATE_USER_GENERATED_KEY,
              label: userGeneratedValueName,
              value: { name: userGeneratedValueName },
              isCreate: false,
            },
          ]
        : [];
    })();

    return [...userGeneratedOption, ...catalogOptions];
  }, [availableOccupations, input, trimmedInput, currentValueName, userGeneratedValueName]);

  /**
   * Select the option the learner picked from the dropdown (catalog or free text).
   * A no-op when it is already the current value: CDS re-fires `onSelectionChange`
   * with the unchanged key on blur and when the learner re-picks the selected
   * option, and the saved free-text option is itself clickable -- without this
   * guard each of those would kick off a redundant debounced save.
   */
  const selectFromOption = (key: string) => {
    const selected = options.find((option) => option.key === key);
    if (selected && !isSameValue(selected.value, value)) onChange(selected.value);
  };

  /**
   * Apply whatever is typed (Enter / blur / add): an exact match in the
   * available occupations replaces the value with that catalog occupation,
   * otherwise a user-generated (free-text) value replaces it. A no-op for
   * whitespace-only input.
   */
  const selectFromInput = () => {
    if (!trimmedInput) return;
    const exactMatch = availableOccupations.find(
      (occupation) => normalize(occupation.name) === normalize(trimmedInput)
    );
    onChange(exactMatch ? { id: exactMatch.id, name: exactMatch.name } : { name: trimmedInput });
  };

  /** Clear the current value (e.g. the learner empties the field and applies it). */
  const clear = () => onChange(null);

  return { options, trimmedInput, selectFromOption, selectFromInput, clear };
};

export default useCurrentRoleEditor;
